import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ConfigService } from '../services/config.service';
import { UsageAnalyticsService } from '../services/usage-analytics.service';
import { Result, ListResponse } from '@shared/models';
import * as Entities from '@shared/entities';

@Injectable({
  providedIn: 'root',
})
export class LeadsApi {
  private serviceUri = '';

  constructor(
    private http: HttpClient,
    private config: ConfigService,
    private usageAnalytics: UsageAnalyticsService
  ) {
    this.config.Config.subscribe((config) => {
      if (config?.apiUri) {
        this.serviceUri = `${config.apiUri}/leads/`;
      }
    });
  }

  search(request: Entities.SearchLeadsRequest): Observable<Result<ListResponse<Entities.LeadListItem>>> {
    return this.http.post<Result<ListResponse<Entities.LeadListItem>>>(this.serviceUri + 'search', request).pipe(
      tap({
        next: (result) => {
          const records = result.successful && result.data ? result.data.records : [];
          this.trackSearch(
            request,
            result.successful ? 'Succeeded' : 'Failed',
            records.length,
            result.data?.totalRecords ?? 0
          );
        },
        error: () => this.trackSearch(request, 'Failed', 0, 0),
      })
    );
  }

  getLead(leadKey: string): Observable<Result<Entities.LeadDetail>> {
    return this.http.get<Result<Entities.LeadDetail>>(this.serviceUri + leadKey);
  }

  private trackSearch(
    request: Entities.SearchLeadsRequest,
    outcome: 'Succeeded' | 'Failed',
    resultCount: number,
    totalRecords: number
  ): void {
    this.usageAnalytics.trackAction('LeadSearch', {
      props: {
        Outcome: outcome,
        HasKeywordSearch: String(!!request.searchTerm),
        HasProviderService: String(!!request.providerService),
        HasRetailerFilter: String(request.retailerId !== undefined),
        HasShiftLeadIds: String(!!request.shiftLeadIds?.length),
        HasSourceLeadIds: String(!!request.sourceLeadIds?.length),
        SortColumn: request.orderBy ?? '',
        SortDirection: request.orderAscending ? 'asc' : 'desc',
      },
      measurements: {
        FilterCount: this.getFilterCount(request),
        LeadStatusCount: request.leadStatusIds?.length ?? 0,
        DateWindowDays: this.getDateWindowDays(request.receivedFrom, request.receivedTo),
        PageNumber: request.pageNumber ?? 1,
        PageSize: request.pageSize ?? 0,
        ResultCount: resultCount,
        TotalRecords: totalRecords,
      },
    });
  }

  private getFilterCount(request: Entities.SearchLeadsRequest): number {
    return [
      request.providerService ? 1 : 0,
      request.retailerId !== undefined ? 1 : 0,
      request.leadStatusIds?.length ? 1 : 0,
      request.shiftLeadIds?.length ? 1 : 0,
      request.sourceLeadIds?.length ? 1 : 0,
      request.receivedFrom ? 1 : 0,
      request.receivedTo ? 1 : 0,
    ].reduce((total, count) => total + count, 0);
  }

  private getDateWindowDays(start?: Date, end?: Date): number {
    if (!start || !end) {
      return 0;
    }

    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    return Math.max(0, Math.ceil((end.getTime() - start.getTime()) / millisecondsPerDay));
  }
}
